import { Component, ViewChild, effect, inject, input } from '@angular/core';
import { CanLeaveGuardComponent } from '@pflegix/guard';
import { UpdateOrderDto } from '@pflegix/types';
import { OrderFormComponent, type OrderFormGroupValue } from '../../../order-form';
import { OrderUpdateStore } from '../../store/order-update.store';

@Component({
  selector: 'pfx-order-update',
  imports: [OrderFormComponent],
  providers: [OrderUpdateStore],
  templateUrl: './order-update.component.html',
  styleUrl: './order-update.component.scss',
})
export class OrderUpdateComponent extends CanLeaveGuardComponent {
  public orderId = input.required<string>();

  @ViewChild(OrderFormComponent) protected orderForm!: OrderFormComponent;

  protected orderUpdateStore = inject(OrderUpdateStore);

  constructor() {
    super();

    effect(() => {
      const orderId = this.orderId();

      this.orderUpdateStore.setOrderId(orderId);
    });
  }

  private static mapFormValueToUpdateOrderDto(formValue: OrderFormGroupValue): UpdateOrderDto {
    return {
      settlementType: formValue.settlementType,
      attendances: formValue.attendances,
      caregiver: formValue.caregiver,
      organization: formValue.organization,
      languagePreference: formValue.languagePreference,
      genderPreference: formValue.genderPreference,
      pets: formValue.pets,
      frequency: OrderUpdateComponent.getFrequencyDtoFromFormValue(formValue.frequency),
      period: OrderUpdateComponent.getPeriodDtoFromFormValue(formValue.period),
    } satisfies UpdateOrderDto;
  }

  private static getPeriodDtoFromFormValue(
    formValue: OrderFormGroupValue['period']
  ): UpdateOrderDto['period'] | undefined {
    if (!formValue) {
      return undefined;
    }

    return {
      ...(formValue.startAt && { startAt: formValue.startAt.toISOString() }),
      ...(formValue.endAt && { endAt: formValue.endAt.toISOString() }),
    } satisfies UpdateOrderDto['period'];
  }

  private static getFrequencyDtoFromFormValue(
    formValue: OrderFormGroupValue['frequency']
  ): UpdateOrderDto['frequency'] | undefined {
    if (!formValue?.rhythm) {
      return undefined;
    }

    return {
      rhythm: formValue.rhythm,
      visits: formValue.visits ?? 1,
      duration: formValue.duration ?? 1,
    } satisfies UpdateOrderDto['frequency'];
  }

  public canDeactivate(): boolean {
    return this.orderForm.isPristine;
  }

  public resetForm(): void {
    this.orderForm.formWrapper.form.reset();
  }

  protected handleSubmit(formValue: OrderFormGroupValue): void {
    this.orderUpdateStore.updateOrder(OrderUpdateComponent.mapFormValueToUpdateOrderDto(formValue)).then(() => {
      this.orderForm.loadingFinished();
    });
  }
}
